import { useMemo, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { buildArtistLeaderboard } from '@/components/leaderboard/leaderboardData';
import LeaderboardTable from '@/components/leaderboard/LeaderboardTable';
import { useNavigate } from 'react-router-dom';
import { Tv } from 'lucide-react';
import type { StudioId } from '@/domain/types';

export default function Leaderboard() {
  const bookings = useBrainStore((s) => s.bookings);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const navigate = useNavigate();
  const [studioId, setStudioId] = useState<StudioId | 'all'>('all');

  const rows = useMemo(
    () =>
      buildArtistLeaderboard(
        bookings,
        artists,
        clockIso,
        studioId === 'all' ? undefined : studioId,
      ),
    [bookings, artists, clockIso, studioId],
  );

  return (
    <section className="px-8 py-8 max-w-5xl" data-testid="leaderboard-screen">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <p className="label-small-caps text-gold-bright">P5 · Studio Health</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Rebook Leaderboard</h1>
          <p className="font-serif text-cream-soft mt-2">
            Artists ranked by in-chair rebooks this month. Streaks count consecutive completed
            sessions that ended with the next one already locked in.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            const target = studioId === 'all' ? Object.values(studios)[0]?.id : studioId;
            if (target) navigate(`/leaderboard/tv/${target}`);
          }}
          className="btn-quiet text-sm"
          data-testid="leaderboard-tv-launch"
        >
          <Tv size={14} /> Launch TV mode
        </button>
      </header>

      <div className="flex gap-2 flex-wrap mb-4" data-testid="leaderboard-studio-filter">
        <FilterChip
          active={studioId === 'all'}
          onClick={() => setStudioId('all')}
          label="All studios"
          testid="leaderboard-filter-all"
        />
        {Object.values(studios).map((s) => (
          <FilterChip
            key={s.id}
            active={studioId === s.id}
            onClick={() => setStudioId(s.id)}
            label={s.name}
            testid={`leaderboard-filter-${s.id}`}
          />
        ))}
      </div>

      <LeaderboardTable rows={rows} />
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={
        active
          ? 'rounded-md border border-gold-bright bg-gold/10 text-gold-bright font-inter text-[11px] px-2.5 py-1.5 uppercase tracking-widest'
          : 'rounded-md border border-gold-dim/30 text-cream-soft hover:border-gold-bright/50 font-inter text-[11px] px-2.5 py-1.5 uppercase tracking-widest transition'
      }
    >
      {label}
    </button>
  );
}
